import { Inject, Injectable, OnDestroy } from '@angular/core';
import { ActivationEnd, Router } from '@angular/router';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../app.config';
import { AuthStatus } from '../auth/auth.model';
import { AuthService } from '../auth/auth.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';
import { User } from '../user/user.model';
import { UserService } from '../user/user.service';

@Injectable({
    providedIn: 'root',
})
@TakeUntilDestroy()
export class NewRelicService implements OnDestroy {
    private readonly _agent: BrowserAgent;

    constructor(
        private readonly _router: Router,
        private readonly _authService: AuthService,
        private readonly _userService: UserService,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {
        const options = {
            init: {
                distributed_tracing: { enabled: true },
                privacy: { cookies_enabled: true },
                ajax: { deny_list: [this._appConfig.NEW_RELIC?.BEACON || ''] },
            },
            info: {
                beacon: this._appConfig.NEW_RELIC?.BEACON,
                errorBeacon: this._appConfig.NEW_RELIC?.ERROR_BEACON,
                licenseKey: this._appConfig.NEW_RELIC?.LICENSE_KEY,
                applicationID: this._appConfig.NEW_RELIC?.APPLICATION_ID,
            },
            loader_config: {
                accountID: this._appConfig.NEW_RELIC?.ACCOUNT_ID,
                trustKey: this._appConfig.NEW_RELIC?.TRUST_KEY,
                agentID: this._appConfig.NEW_RELIC?.AGENT_ID,
                licenseKey: this._appConfig.NEW_RELIC?.LICENSE_KEY,
                applicationID: this._appConfig.NEW_RELIC?.APPLICATION_ID,
            },
        };

        this._agent = new BrowserAgent(options);
        this._agent?.setApplicationVersion?.(CMPS_APP_DEF.BUILD);

        this._bindAuth();

        this._bindNavigation();
    }

    ngOnDestroy(): void {}

    private _startTrackUser(user: User): void {
        this._agent?.setUserId?.(`${user.id}`);
        this._agent?.setCustomAttribute?.(
            'cmps.trackByUser',
            `${user.firstName}-${user.id}`,
        );
    }

    private _stopTrackUser(): void {
        this._agent?.setCustomAttribute?.('cmps.trackByUser', null);
        this._agent?.setUserId?.(null);
    }

    private _bindAuth(): void {
        this._authService.authStatus
            .pipe(
                switchMap((authStatus) => {
                    if (authStatus === AuthStatus.AUTHENTICATED) {
                        /* TODO revise. authService doesn't guarantee user is populated. authService should be changed */
                        return this._userService.retrieve().pipe(
                            tap((user) => {
                                this._startTrackUser(user);
                            }),
                            catchError(() => {
                                return of(null);
                            }),
                        );
                    } else {
                        return of(null).pipe(
                            tap(() => {
                                this._stopTrackUser();
                            }),
                        );
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _trackRoute(): void {
        let path: string | null = null;
        try {
            let ar = this._router.routerState.root;
            const paths = [''];
            while (ar != null) {
                if (
                    ar.routeConfig?.path != null &&
                    ar.routeConfig?.path !== ''
                ) {
                    paths.push(ar.routeConfig?.path);
                }
                ar = ar.firstChild;
            }
            path = paths.join('/');
        } catch {
            // nothing
        }

        this._agent?.setCurrentRouteName?.(path);
        this._agent?.setCustomAttribute?.('cmps.fullUrl', window.location.href);
    }

    private _bindNavigation(): void {
        this._router.events
            .pipe(
                tap((event) => {
                    /* must be before NavigationEnd to be applied to the current browser interaction */
                    if (event instanceof ActivationEnd) {
                        this._trackRoute();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
